import React, { useState, useEffect, useMemo } from 'react';
import DataProvider from '../../provider';

import { makeStyles, Theme } from '@material-ui/core/styles';
import Funnel from '../../templates/contents/Funnel';
import { useParams } from 'react-router-dom';
import CartDrawer from './cart/CartDrawer';
import {CartPreview} from './cart/CartPreview';
import CartDialog from './cart/CartDialog';

import { Provider, useSelector, useDispatch } from 'react-redux';
import { createStore } from 'redux';
import sellingAreaApp from './state/reducers';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useHistory, useLocation } from 'react-router-dom';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import InternalPayments from './InternalPayments';
import Banner from './Banner';
import UnsellableItemInfoPopup from './UnsellableItemInfoPopup';
import { pushEvent } from '../../utils/gtmDataLayer';

import DialogPreference from './components/DialogPreference';

import {
	cartAction,
	updateCatalog,
	updateActions,
	updateStatus,
	updateData,
	updateWalletBalance,
	updateCorporateCreditBalance,
	emptyCorporateCreditBalance,
	updateBanner,
	DietAction,
	updateCartBehavior,
	updateBundle,
	message as messageAction,
} from './state/actions';
import Catalog from './Catalog';
import BundlePage from './bundle/BundlePage';
import Feedback from './Feedback';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { SellingAreaStatus } from './state';
import { ActionPaymentPreferences } from './state/interfaces/index';
import { LockerConfig, LockerSellingOrderCartItem } from './interfaces';
import moment from 'moment';
import BundleRecapPage from './bundle/BundleRecapPage';
import OpenAppBanner from '../../components/OpenAppBanner';
import SellingAreaOpenAppDialog from './SellingAreaOpenAppDialog';
import { AppPenetrationAction } from '../../models';


export const drawerWidth = 320;

const useStyles = (cart) => makeStyles((theme: Theme) => ({
	root: {
		width: '100%',
		display: 'flex'
	},
	container: {
		width: '100%',
		[theme.breakpoints.up('md')]: {
			paddingRight: cart ? drawerWidth : 0
		},
		[theme.breakpoints.down('sm')]: {
			paddingRight: 0
		},
		[theme.breakpoints.down('sm')]: {
			paddingBottom: cart ? 240 : 0
		},
	}
}));

const SellingAreaComponent = (props: any) => {
	let params = useParams();
	let location = useLocation();
	const mySearchParams = new URLSearchParams(location.search);
	let id = params['id'];
	const [ showModal, setShowModal] = useState<boolean>(false);
	// undefined until the configs answer, so the old banner never flashes before the dialog.
	const [ appPenetrationAction, setAppPenetrationAction] = useState<AppPenetrationAction | null | undefined>(undefined);
	const message = useSelector(state => state.message);
	const cart = useSelector(state => state.cart);
	const status = useSelector(state => state.status);
	const sellingAreaData = useSelector(state => state.data);
	const catalog = useSelector(state => state.catalog);
	const dispatch = useDispatch();
	let history = useHistory();
	const isBundlePage = location.pathname.includes('/bundle/');
	const classes = useStyles(cart && !isBundlePage ? true : false)();
	const BUNDLE_CART_LABEL = 'bundle-cart';
	const AWAITING_PAYMENT_ACTION = 'awaitingPayment';

	const { path } = useRouteMatch();
	const dataProvider = useMemo(() => DataProvider(process.env.REACT_APP_WAREHOUSE_API || ''), []);
	const wasBundlePage = React.useRef<boolean | null>(null);
	useEffect(() => {
		if (wasBundlePage.current === true && !isBundlePage) {
			dispatch(updateStatus(SellingAreaStatus.FETCH_DATA));
		}
		wasBundlePage.current = isBundlePage;
	}, [isBundlePage, dispatch]);

	useEffect(() => {
		if(!id) {
			return;
		}

		if(SellingAreaStatus.FETCH_DATA === status) {
			dispatch(updateStatus(SellingAreaStatus.FETCHING_DATA))
			const localDateTime = moment().format('YYYY-MM-DDTHH:mm:ss');

			dataProvider(`b2c/locker/${id}/configs?customerDateTime=${localDateTime}`)
				.then((data: LockerConfig) => {
					dispatch(updateData({
						title: data.title
					}));
					setAppPenetrationAction(data.appPenetrationAction ?? null);

					if(data.actions) {
						dispatch(updateActions(data.actions));
					}
					if(data.catalog) {
						pushEvent({
							type: "ViewItemList",
							data: {
								...data.catalog,
								sellingPointImpulseId: id,
							},
							dataLabel: "b2cImpulseCatalog",
						});
						dispatch(updateCatalog(data.catalog));
					}

					if(data.catalog.shopStatus === 'Close') {
						return null;
					}

					const orderItems = data.order?.lockerSellingOrder?.items ?? [];
					const bundleItemsToClean: LockerSellingOrderCartItem[] = (data.order && data.order.actionType !== AWAITING_PAYMENT_ACTION)
						? orderItems.filter((item) => item.featureName === BUNDLE_CART_LABEL)
						: [];

					if(data.order) {
						if (bundleItemsToClean.length === 0) {
							setShowModal(data.order.actionType === AWAITING_PAYMENT_ACTION);
							dispatch(cartAction.updateCart(data.order, data.wallet ? data.wallet.balance : 0, data.corporateCreditWallet ? data.corporateCreditWallet.balance : 0));
						}
					} else {
						setShowModal(false);
						dispatch(cartAction.emptyCart());
					}

					if(data.wallet) {
						dispatch(updateWalletBalance(data.wallet.balance));
					}
					if(!data.actions && !data.catalog) {
						dispatch(updateStatus(SellingAreaStatus.NOT_ACTIVE))
						return null;
					}

					if(data.corporateCreditWallet && data.corporateCreditWallet.balance > 0) {
						dispatch(updateCorporateCreditBalance(data.corporateCreditWallet.balance));
					} else {
						dispatch(emptyCorporateCreditBalance());
					}

					if(data.paymentPreferences) {
						const updatePreferences: ActionPaymentPreferences = {
							type: 'UPDATE_PAYMENT_PREFERECES',
							preferences: data.paymentPreferences,
						};
						dispatch(updatePreferences);
					}

					if(data.dietPreference) {
						const updateDietPreference: DietAction = {
							type: 'UPDATE_DIET_PREFERENCE',
							data: data.dietPreference,
						};
						dispatch(updateDietPreference);
					}

					if(data.banner) {
						dispatch(updateBanner(data.banner));
					}

					if(data.cartBehavior) {
						dispatch(updateCartBehavior(data.cartBehavior));
					}

					if(data.catalog?.bundles) {
							dispatch(updateBundle(
								data.catalog?.bundles?.map(b => ({
									...b,
									categories: b.categories.map((c: { categoryId: number }) => c.categoryId),
									excludedByCategory: b.categories.reduce(
										(acc: Record<number, number[]>, c: { categoryId: number; excludeInternalCategoryIds?: number[] }) => {
											acc[c.categoryId] = c.excludeInternalCategoryIds ?? [];
											return acc;
										},
										{},
									),
							}))
						));
					}

					if (bundleItemsToClean.length > 0) {
						Promise.all(
							bundleItemsToClean.map((item) =>
								dataProvider(`b2c/locker/selling-order/edit-item`, {
									method: 'POST',
									body: JSON.stringify({ lockerId: id, itemSaleableId: item.itemSaleableId, quantity: 0 })
								})
							)
						).then(() => {
							dispatch(cartAction.emptyCart());
							dispatch(updateStatus(SellingAreaStatus.DATA_FETCHED));
						}).catch(() => {
							messageAction.show(dispatch, 'C\'è stato un problema con il carrello menù precedente. Puoi ricaricare la pagina o continuare con gli altri prodotti.', 8000);
							dispatch(updateStatus(SellingAreaStatus.DATA_FETCHED));
						});
					} else {
						dispatch(updateStatus(SellingAreaStatus.DATA_FETCHED));
					}
				})
				.catch(e => {
					setAppPenetrationAction(null);
					if(e === 'Not Found') {
						dispatch(updateStatus(SellingAreaStatus.NOT_FOUND))
					}
					else {
						dispatch(updateStatus(SellingAreaStatus.NOT_ACTIVE))
					}
				})
			}

	}, [id, dataProvider, dispatch, status]);

	return (
		<Funnel back={() => history.push('/')} rightAction={{
			icon:'person',
			onClick: () => {history.push('/me');}
		}} filter={catalog && catalog.shopStatus === "Open" ? {
			icon:'filter_list',
			onClick: () => {
				if(location.search.length > 0) {
					history.push(`${location.search}&diet_preference=show`)
				}
				else {
					history.push(`?diet_preference=show`)
				}
			}
		}: null} title={sellingAreaData ? sellingAreaData.title : ""} loading={SellingAreaStatus.FETCH_DATA === status}>
			<div className={classes.root}>
				<div className={classes.container}>
					{mySearchParams.get('mode') !== 'preview' && <InternalPayments />}
					<Banner />
					<UnsellableItemInfoPopup />
					<Switch>
						<Route path={`${path}/bundle/:bundleId/recap`} component={BundleRecapPage} />
						<Route path={`${path}/bundle/:bundleId`} component={BundlePage} />
						<Route exact path={path} component={Catalog} />
					</Switch>
					<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={message}>
						<Alert severity="warning">
							{message}
						</Alert>
					</Snackbar>
				</div>
				{mySearchParams.get('mode') !== 'preview' && !isBundlePage && <CartDrawer></CartDrawer>}
			</div>
			<Feedback />
			{SellingAreaStatus.DATA_FETCHED === status && <DialogPreference />}
			{mySearchParams.get('mode') !== 'preview' && !isBundlePage && <CartPreview></CartPreview>}
			{mySearchParams.get('mode') !== 'preview' && <CartDialog></CartDialog>}
			{mySearchParams.get('mode') !== 'preview' && <Dialog open={showModal} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        			<DialogTitle id="alert-dialog-title">{"Pagamento in corso"}</DialogTitle>
        			<DialogContent>
        			  <DialogContentText id="alert-dialog-description">
        			    Hai un pagamento in corso, scegli se continuare.
        			  </DialogContentText>
        			</DialogContent>
        			<DialogActions>
						<Button onClick={()=> {
							const paymentUUID = cart.paymentUUID;
							setShowModal(false);
							dispatch(updateStatus(SellingAreaStatus.FETCHING_DATA));
							dataProvider(`b2c/payment/${paymentUUID}/cancel`)
								.then(info => {
									dispatch(updateStatus(SellingAreaStatus.FETCH_DATA));
								})
								.catch(err => {
									dispatch(updateStatus(SellingAreaStatus.FETCH_DATA));
								});
						}} color="primary">
        			    Modifica ordine
        			  </Button>
        			  <Button onClick={()=> history.push(`/payment/${cart.paymentUUID}`)} variant="contained" size='large' color="primary">
        			    Continua
        			  </Button>
        			</DialogActions>
      			</Dialog>
      }
      {appPenetrationAction === null && (mySearchParams.get('mode') === "preview" || !cart) && <OpenAppBanner style={{bottom: 0}} sellingPointImpulseUuid={id} />}
      {appPenetrationAction && <SellingAreaOpenAppDialog key={id} action={appPenetrationAction} showPaymentModal={showModal} />}
		</Funnel>
	);
}

export default function SellingArea(props: any) {
	const store = createStore(sellingAreaApp);

	return <Provider store={store}>
    <SellingAreaComponent {...props}></SellingAreaComponent>
	</Provider>
}
